import { useEffect, useState } from "react";
import { Sun, Cloud, CloudRain, CloudSnow, CloudLightning, CloudFog, Pencil } from "lucide-react";

const STORAGE_KEY = "np_weather_city";
const DEFAULT_CITY = "Melun, France";

// Codes météo Open-Meteo (WMO) -> icône + libellé FR
function weatherFromCode(code) {
  if (code === 0) return { label: "Ciel dégagé", Icon: Sun };
  if ([1, 2, 3].includes(code)) return { label: "Partiellement nuageux", Icon: Cloud };
  if ([45, 48].includes(code)) return { label: "Brouillard", Icon: CloudFog };
  if ([51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82].includes(code)) return { label: "Pluie", Icon: CloudRain };
  if ([71, 73, 75, 77, 85, 86].includes(code)) return { label: "Neige", Icon: CloudSnow };
  if ([95, 96, 99].includes(code)) return { label: "Orage", Icon: CloudLightning };
  return { label: "Météo", Icon: Cloud };
}

function pad(n) {
  return String(n).padStart(2, "0");
}

export default function DateTimeWeather() {
  const [now, setNow] = useState(new Date());
  const [city, setCity] = useState(() => localStorage.getItem(STORAGE_KEY) || DEFAULT_CITY);
  const [editingCity, setEditingCity] = useState(false);
  const [cityInput, setCityInput] = useState(city);
  const [weather, setWeather] = useState(null);
  const [weatherError, setWeatherError] = useState(false);

  // Horloge : mise à jour chaque minute (format JJ/MM/AAAA — HH:MM)
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000 * 30);
    return () => clearInterval(t);
  }, []);

  // Météo réelle via Open-Meteo (géocodage + prévisions actuelles), rafraîchie toutes les 15 min
  useEffect(() => {
    let cancelled = false;

    async function fetchWeather() {
      try {
        setWeatherError(false);
        const geoRes = await fetch(
          `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(city)}&count=1&language=fr&format=json`
        );
        const geo = await geoRes.json();
        const place = geo?.results?.[0];
        if (!place) throw new Error("Ville introuvable");

        const wRes = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${place.latitude}&longitude=${place.longitude}&current=temperature_2m,apparent_temperature,weather_code&timezone=auto`
        );
        const wData = await wRes.json();
        if (cancelled) return;
        setWeather({
          temp: Math.round(wData.current.temperature_2m),
          feelsLike: Math.round(wData.current.apparent_temperature),
          code: wData.current.weather_code,
          cityLabel: place.name,
        });
      } catch (e) {
        if (!cancelled) setWeatherError(true);
      }
    }

    fetchWeather();
    const interval = setInterval(fetchWeather, 15 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [city]);

  const dateStr = `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()}`;
  const timeStr = `${pad(now.getHours())}:${pad(now.getMinutes())}`;

  const saveCity = () => {
    const value = cityInput.trim() || DEFAULT_CITY;
    setCity(value);
    localStorage.setItem(STORAGE_KEY, value);
    setEditingCity(false);
  };

  const { label, Icon } = weather ? weatherFromCode(weather.code) : { label: "", Icon: Cloud };

  return (
    <div
      className="flex items-center gap-4 px-4 py-2 border-b border-gray-200 bg-white text-sm text-gray-700"
      data-testid="datetime-weather-bar"
    >
      <div className="flex items-center gap-2 font-medium tabular-nums" data-testid="current-datetime">
        <span>{dateStr}</span>
        <span className="text-gray-400">—</span>
        <span>{timeStr}</span>
      </div>

      <div className="w-px h-4 bg-gray-200" />

      <div className="flex items-center gap-2" data-testid="weather-widget">
        {weatherError ? (
          <span className="text-gray-400">Météo indisponible</span>
        ) : !weather ? (
          <span className="text-gray-400">Chargement météo…</span>
        ) : (
          <>
            <Icon className="w-4 h-4 text-sky-600" strokeWidth={1.8} />
            <span>
              {weather.cityLabel} · {weather.temp}°C ({label.toLowerCase()})
              {weather.feelsLike != null && (
                <span className="text-gray-400"> · ressenti {weather.feelsLike}°C</span>
              )}
            </span>
          </>
        )}

        {editingCity ? (
          <div className="flex items-center gap-1 ml-1">
            <input
              autoFocus
              value={cityInput}
              onChange={(e) => setCityInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && saveCity()}
              onBlur={saveCity}
              className="border border-gray-300 rounded px-1.5 py-0.5 text-xs w-32"
              placeholder="Ville"
              data-testid="weather-city-input"
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => {
              setCityInput(city);
              setEditingCity(true);
            }}
            className="text-gray-400 hover:text-gray-700 ml-0.5"
            title="Changer la ville"
            data-testid="weather-city-edit-btn"
          >
            <Pencil className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}
